import { useEffect, useState, useCallback } from "react";
import { crmApi } from "@/lib/crm";
import { Card, Stat, Btn, Input, SectionTitle, Empty, Table, Pill } from "./kit";

function FeedManager() {
  const [posts, setPosts] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => crmApi("/social/feed").then(setPosts).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    setNote("");
    try {
      await crmApi("/social/feed", { method: "POST", body: { url, caption } });
      setUrl(""); setCaption("");
      setNote("Post added — live on the storefront now");
      load();
    } catch (err) { setNote(err.response?.data?.detail || "Failed to add post"); }
  };
  const toggle = async (p) => { await crmApi(`/social/feed/${p.id}`, { method: "PATCH", body: { active: !p.active } }); load(); };
  const remove = async (p) => { await crmApi(`/social/feed/${p.id}`, { method: "DELETE" }); load(); };
  return (
    <div>
      <p className="text-xs text-slate-500 mb-4">
        Paste a public Instagram post/reel or TikTok video link — it appears as a live embed on the storefront's
        "Latest from NOXWAY" section. Remove one and it's gone instantly.
      </p>
      <form onSubmit={add} className="flex flex-wrap gap-2.5 mb-5">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.instagram.com/reel/… or tiktok.com/@…/video/…" className="flex-[2] min-w-56" required data-testid="feed-url-input" />
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="flex-1 min-w-40" data-testid="feed-caption-input" />
        <Btn type="submit" data-testid="feed-add">Add to feed</Btn>
      </form>
      {note && <p className="text-sm text-[#f97316] mb-4" data-testid="feed-note">{note}</p>}
      {posts.length === 0 ? <Empty>Feed is empty — storefront section stays hidden until you add a post</Empty> : (
        <ul className="space-y-2.5">
          {posts.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
              <Pill tone="orange">{p.platform}</Pill>
              <span className="text-xs text-slate-400 flex-1 min-w-0 truncate">{p.caption || p.source_url}</span>
              {p.active ? <Pill tone="green">live</Pill> : <Pill>hidden</Pill>}
              <Btn onClick={() => toggle(p)} data-testid="feed-toggle">{p.active ? "Hide" : "Show"}</Btn>
              <Btn onClick={() => remove(p)} data-testid="feed-remove">Remove</Btn>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IgSyncCard() {
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => crmApi("/social/instagram/status").then(setStatus).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const connect = async () => {
    setNote("");
    try {
      const { url } = await crmApi("/social/instagram/auth-url");
      window.open(url, "_blank");
    } catch (e) { setNote(e.response?.data?.detail || "Not configured"); }
  };
  const syncNow = async () => {
    setNote("");
    try {
      const r = await crmApi("/social/instagram/sync", { method: "POST" });
      setNote(`Synced ${r.synced} posts (${r.new} new)`);
      load();
    } catch (e) { setNote(e.response?.data?.detail || "Sync failed"); }
  };
  if (!status) return null;
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid="ig-sync-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-sm font-semibold">Instagram auto-sync</span>
        {status.connected
          ? <Pill tone="green">connected{status.username ? ` @${status.username}` : ""}</Pill>
          : <Pill tone="amber">not connected</Pill>}
        {status.connected && <Btn onClick={syncNow} data-testid="ig-sync-now">Sync now</Btn>}
        {!status.connected && <Btn onClick={connect} data-testid="ig-connect">Connect Instagram</Btn>}
      </div>
      {note && <p className="text-sm text-[#f97316] mb-3" data-testid="ig-note">{note}</p>}
      {!status.configured && (
        <p className="text-xs text-slate-500 leading-relaxed">
          Auto-sync needs a one-time Meta setup: make @noxway888 a Business/Creator account, create a Meta
          developer app with "Instagram API with Instagram login", then give your developer the App ID + App Secret.
          Once connected, your latest posts flow into the storefront feed automatically every 15 minutes.
        </p>
      )}
      {status.connected && (
        <p className="text-xs text-slate-500">
          {status.auto_posts} auto-synced posts · last sync {status.last_sync ? status.last_sync.slice(0, 16).replace("T", " ") : "never"} · runs automatically every 15 minutes
        </p>
      )}
    </div>
  );
}

export function SocialTab() {
  const [creators, setCreators] = useState([]);
  const [ugc, setUgc] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const load = useCallback(() => {
    crmApi("/social/creators").then(setCreators).catch(() => {});
    crmApi("/social/ugc").then(setUgc).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const setCreatorStatus = async (id, status) => { await crmApi(`/social/creators/${id}`, { method: "PATCH", body: { status } }); load(); };
  const setUgcStatus = async (id, status) => { await crmApi(`/social/ugc/${id}`, { method: "PATCH", body: { status } }); load(); };

  return (
    <div className="space-y-6" data-testid="crm-social">
      <Card>
        <SectionTitle>Creators</SectionTitle>
        {creators.length === 0 ? <Empty>No creator applications yet — they arrive from /community</Empty> : (
          <Table cols={["Name", "Platform", "Status", "Code", "Clicks", "Orders", "Revenue", "Commission owed", "Payout", ""]}>
            {creators.map((c) => (
              <tr key={c.id}>
                <td className="py-2.5 pr-4">{c.name}<div className="text-xs text-slate-500">{c.email}</div></td>
                <td className="py-2.5 pr-4 text-xs">{c.platform} {c.handle}</td>
                <td className="py-2.5 pr-4">
                  <Pill tone={c.status === "approved" ? "green" : c.status === "rejected" ? "red" : "amber"}>{c.status}</Pill>
                </td>
                <td className="py-2.5 pr-4 font-tech text-xs text-[#f97316]">{c.referral_code || "—"}</td>
                <td className="py-2.5 pr-4">{c.clicks}</td>
                <td className="py-2.5 pr-4">{c.orders}</td>
                <td className="py-2.5 pr-4">CA${(c.revenue || 0).toLocaleString()}</td>
                <td className="py-2.5 pr-4">CA${(c.commission_owed || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="py-2.5 pr-4 text-xs text-slate-500">{c.payout_status}</td>
                <td className="py-2.5 space-x-2 whitespace-nowrap">
                  {c.status !== "approved" && <Btn onClick={() => setCreatorStatus(c.id, "approved")} data-testid="creator-approve">Approve</Btn>}
                  {c.status !== "rejected" && <Btn onClick={() => setCreatorStatus(c.id, "rejected")} data-testid="creator-reject">Reject</Btn>}
                </td>
              </tr>
            ))}
          </Table>
        )}
        <p className="text-xs text-slate-600 mt-4">Approving a creator issues their referral code. Commissions accrue on paid attributed orders; payouts stay manual until a payout provider is verified.</p>
      </Card>

      <Card>
        <SectionTitle>UGC submissions</SectionTitle>
        {ugc.length === 0 ? <Empty>No UGC submitted yet</Empty> : (
          <ul className="space-y-3">
            {ugc.map((u) => (
              <li key={u.id} className="bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3.5 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{u.name} <span className="text-xs text-slate-500 font-normal">{u.email}</span></div>
                  <a href={u.url} target="_blank" rel="noreferrer" className="text-xs text-[#f97316] break-all">{u.url}</a>
                  {u.description && <div className="text-xs text-slate-500 mt-1">{u.description}</div>}
                </div>
                <Pill tone={u.status === "approved" ? "green" : u.status === "rejected" ? "red" : "amber"}>{u.status}</Pill>
                {u.status === "pending" && (
                  <span className="space-x-2">
                    <Btn onClick={() => setUgcStatus(u.id, "approved")} data-testid="ugc-approve">Approve</Btn>
                    <Btn onClick={() => setUgcStatus(u.id, "rejected")} data-testid="ugc-reject">Reject</Btn>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <SectionTitle>Storefront social feed</SectionTitle>
        <IgSyncCard />
        <div className="mt-5">
          <FeedManager />
        </div>
      </Card>

      <Card>
        <SectionTitle>Campaign generator</SectionTitle>
        <Btn onClick={async () => setCampaign(await crmApi("/social/campaign", { method: "POST" }))} data-testid="campaign-generate">
          Generate hooks + captions
        </Btn>
        {campaign && (
          <div className="grid md:grid-cols-3 gap-4 mt-5" data-testid="campaign-result">
            {[["Hooks", campaign.hooks], ["Captions", campaign.captions], ["Outreach", campaign.outreach]].map(([label, items]) => (
              <div key={label}>
                <div className="font-tech text-[9px] tracking-[0.2em] uppercase text-slate-500 mb-2">{label}</div>
                <ul className="space-y-2">{items.map((i) => <li key={i} className="text-xs text-slate-300 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2">{i}</li>)}</ul>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function GrowthTab() {  const [data, setData] = useState(null);
  useEffect(() => { crmApi("/growth/overview").then(setData).catch(() => {}); }, []);
  if (!data) return <Empty>Loading…</Empty>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="crm-growth">
      <Stat label="Members" value={data.members} sub="rewards customers" />
      <Stat label="Referral orders" value={data.referral_orders} />
      <Stat label="Assistant questions" value={data.assistant_questions} />
      <Stat label="Upsell accepts" value={data.upsell_accepts} />
      <Stat label="Bundle revenue" value={`CA$${data.bundle_revenue_cad.toLocaleString()}`} sub="2 & 3 bag orders" />
    </div>
  );
}
